<script lang="ts">
import { computed, defineComponent, inject, PropType, ref } from "vue";
import Steps from "@/components/Steps.vue";
import formatUtils from "@/businesslogic/utils/formatUtils";
import Abschluss from "@/components/abschluss/Abschluss.vue";
import Modal from "@/components/Modal.vue";
import Zeile from "@/components/grid/Zeile.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import { NavvItem } from "@/model/NavItem";
import IconSprite from "@/components/IconSprite.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "AbschlussPHV",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    Zeile,
    TitelZelle,
    Zelle,
    Modal,
    Abschluss,
    Steps,
    IconSprite,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
  },
  setup(props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");

    const fehler = ref({} as any);

    const { formatCurrency } = formatUtils();

    const hatZusatzmodule = computed(() => {
      return piniaStore.state.tarifinformationen.hundehalterhaftpflicht;
    });

    const getPhvDeckungssumme = computed(() => {
      if (piniaStore.state.tarifinformationen.versicherungssumme) {
        switch (piniaStore.state.tarifinformationen.versicherungssumme) {
          case 100:
            return "10 Mio. €";
          case 200:
            return "20 Mio. €";
          case 500:
            return "50 Mio. €";
          case 1000:
            return "100 Mio. €";
        }
      }
      return "";
    });

    function geheZuVergleich() {
      emit("goto-page", { pageIndex: 1 });
    }

    return {
      props,
      piniaStore,
      fehler,
      formatCurrency,
      wechsleSichtbarkeit,
      istSichtbar,
      hatZusatzmodule,
      getPhvDeckungssumme,
      geheZuVergleich,
    };
  },
});
</script>

<template>
  <Steps :active="3" v-bind:items="navItems" @goto-page="$emit('goto-page', $event)"></Steps>

  <abschluss
    produkt="phv"
    :zeige-versicherte-person="true"
    @next-page="$emit('next-page', $event)"
    @goto-page="$emit('goto-page', $event)"
  >
    <template v-slot:info-1>
      <zeile class="u-mar-t-2">
        <titel-zelle :ausrichtung-responsiv="false" :breite="3" :vertikal-zentrieren="false" class="o-grid__cell--4">
          De&shy;ckungs&shy;sum&shy;me&nbsp;
          <a
            role="button"
            tabindex="0"
            class="u-copy-s field-edit"
            @click="geheZuVergleich"
            @keydown.enter="geheZuVergleich"
            @keydown.space.prevent="geheZuVergleich"
            aria-label="Deckungssumme bearbeiten"
            >bearbeiten</a
          >
        </titel-zelle>
        <zelle :breite="9" class="o-grid__cell--8" data-cy="deckungssumme">
          <strong>{{ getPhvDeckungssumme }}</strong>
        </zelle>
      </zeile>
    </template>

    <template v-if="hatZusatzmodule" v-slot:zusatzmodule><br />mit Hundehalterhaftpflicht </template>
    <template v-slot:angaben-bestaetigen>
      Ja, der Antrag wurde gemäß meinen Angaben erstellt.
      <span v-if="hatZusatzmodule"
        >Mein Hund ist kein
        <a @click.stop.prevent="wechsleSichtbarkeit('kampfUndListenhunde')">Kampf- oder Listenhund</a>
        einschließlich Kreuzungen aus diesen Rassen.
      </span>
      Auf eine Beratung habe ich verzichtet und damit auf den Schadenersatzanspruch nach §6. Abs. 5 VVG.
    </template>
    <template v-if="piniaStore.state.isMakler && hatZusatzmodule" v-slot:schlusserklaerungMakler2>
      <input
        type="checkbox"
        class="c-form-control-checkbox__input"
        id="schlusserklaerungMakler2"
        name="schlusserklaerungMakler2"
        required
        aria-required="true"
        aria-label="Bitte bestätigen Sie die Erklärung."
        data-fehlermeldung="Bitte bestätigen Sie die Erklärung."
        data-cy="schlusserklaerungMakler2"
      />
      <label class="c-form-control-checkbox__label" for="schlusserklaerungMakler2">
        <icon-sprite :ist-klein="true" css-klassen="c-form-control-checkbox__icon" icon-name="icon-check-mark" />
        <span class="c-form-control-checkbox__label-text">
          Der/die Versicherungsmakler/in bestätigt, dass die Hunderasse geprüft wurde. Der Hund seines Mandanten ist
          kein
          <a @click.stop.prevent="wechsleSichtbarkeit('kampfUndListenhunde')"> Kampf- oder Listenhund </a>
          einschließlich Kreuzungen aus diesen Rassen.
        </span>
      </label>
    </template>
  </abschluss>

  <modal
    id="kampfUndListenhunde"
    v-if="istSichtbar('kampfUndListenhunde')"
    titel="Kampf- und Listenhunde"
    text="Alano, American Bulldog, (American) Pit Bull Terrier, American
                Staffordshire Terrier, Bandog, Bullmastiff, Bullterrier, Cane
                Corso / Cane Corso Italiano, Dobermann, Dogo Argentino, Dogue de
                Bordeaux (Boreauxdogge), Fila Brasileiro, Kangal, Kaukasischer
                Owtscharka, Mastiff, Mastin Espanol, Mastino Napoletano, Perro
                de Presa Canario (Dogo Canario), Perro de Presa Mallorquin (Ca
                de Bou), Rottweiler, Staffordshire Bullterrier, Tosa Inu, sowie
                Kreuzungen der genannten Rassen untereinander oder mit anderen
                Hunden."
    @closeModal="wechsleSichtbarkeit('kampfUndListenhunde')"
  />
</template>
